/**
 *  This component provides elaboration and selection for how an item IEP list is to be filtered.
 */
import React, { useEffect, useState } from 'react'

import clsx from 'clsx'
import PropTypes from 'prop-types'
import makeStyles from '@mui/styles/makeStyles'

import { PLAN_TYPES } from '../../arch/iepConstants'

import { PLAN_DEFAULT, PLAN_VARIANTS } from '../constants'

import allThumbnail from '../../assets/iep-items-all.svg'
import allThumbnailDisabled from '../../assets/iep-items-all-disabled.svg'
import allThumbnailSelected from '../../assets/iep-items-all-selected.svg'
import filteredThumbnail from '../../assets/iep-items-filtered.svg'
import filteredThumbnailDisabled from '../../assets/iep-items-filtered-disabled.svg'
import filteredThumbnailSelected from '../../assets/iep-items-filtered-selected.svg'

import SelectableButton from '../../subcomponents/brand/SelectableButton'

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      rowGap: theme.spacing(2)
    },

    title: {
      margin: 0,
      textAlign: 'center'
    },

    filterContainer: {
      columnGap: theme.spacing(3),
      display: 'flex'
    },

    filterButton: {
      borderRadius: theme.spacing(1),
      flexBasis: '50%',
      flexDirection: 'column',
      padding: theme.spacing(3, 4)
    },

    filterIcon: {
      height: theme.spacing(6),
      width: theme.spacing(6)
    },

    filterEndIcon: {
      right: theme.spacing(-1),
      top: theme.spacing(-1.5)
    },

    colorInherit: {}, // Mainly for tagging, below.

    filterButtonSelected: {
      '&$colorInherit': {
        backgroundColor: 'inherit'
      }
    },

    thumbnail: {
      height: theme.spacing(8.75),
      width: theme.spacing(8.75)
    },

    noFilter: {},
    withFilter: {},

    label: {
      ...theme.typography.body1,

      fontWeight: theme.typography.fontWeightMedium,
      padding: theme.spacing(1, 0, 0),
      whiteSpace: 'nowrap'
    },

    sublabel: {
      ...theme.typography.caption,

      color: theme.palette.text.secondary
    },

    annotationContainer: {
      backgroundColor: 'rgba(240, 251, 255, 0.60)',
      borderColor: theme.palette.border,
      borderRadius: theme.spacing(1),
      borderStyle: 'solid',
      borderWidth: theme.spacing(0.125),
      color: theme.palette.text.primary,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1, 2)
    },

    annotationTitle: {
      ...theme.typography.body1,

      fontWeight: theme.typography.fontWeightMedium
    },

    annotation: {
      ...theme.typography.body2
    }
  }),
  {
    name: 'IepFilterSelector'
  }
)

const EMPTY = [] // Constant for empty list to minimize re-renders.

export const FILTER_ALL = 'all'
export const FILTER_WITH_RECOMMENDATIONS_ONLY = 'with-recommendations-only'

const FilterSelector = props => {
  const {
    className,
    planType,
    all = EMPTY,
    filtered = EMPTY,
    childName = 'your child',
    nounSingular,
    nounPlural,
    onChange
  } = props

  const planVariant = PLAN_VARIANTS[planType] ?? PLAN_DEFAULT
  const { planLabel } = planVariant

  const allCount = all.length
  const filteredCount = filtered.length

  const allRecommendations = allCount === filteredCount
  const noRecommendations = filteredCount === 0
  const noun = allCount === 1 ? nounSingular : nounPlural

  const [filter, setFilter] = useState(null)

  const filterAll = filter === FILTER_ALL
  const filterWithRecommendationsOnly = filter === FILTER_WITH_RECOMMENDATIONS_ONLY

  const handleSelectAll = event => setFilter(FILTER_ALL)
  const handleSelectWithRecommendationsOnly = event => setFilter(FILTER_WITH_RECOMMENDATIONS_ONLY)

  useEffect(() => {
    setFilter(
      allRecommendations || noRecommendations
        ? allRecommendations
          ? FILTER_WITH_RECOMMENDATIONS_ONLY
          : FILTER_ALL
        : null
    )
  }, [allRecommendations, noRecommendations])

  useEffect(() => {
    // We’d rather do this in an event handler, but must use an effect here to make sure that the
    // caller gets the right initial value.
    if (onChange) {
      onChange(filter)
    }
  }, [filter, onChange])

  const classes = useStyles(props)

  const filterClasses = {
    root: classes.filterButton,
    icon: classes.filterIcon,
    endIcon: classes.filterEndIcon,
    colorInherit: classes.colorInherit,
    selected: classes.filterButtonSelected
  }

  return (
    <section className={clsx(classes.root, className)}>
      <h4 className={classes.title}>How do you want to continue?</h4>

      <section className={classes.filterContainer}>
        <SelectableButton
          classes={filterClasses}
          selected={filterAll}
          disabled={allRecommendations}
          onClick={handleSelectAll}
        >
          <img
            className={clsx(classes.thumbnail, classes.noFilter)}
            src={allRecommendations ? allThumbnailDisabled : filterAll ? allThumbnailSelected : allThumbnail}
            alt="Review all"
          />

          <span className={classes.label}>
            Review {allCount > 0 && (allCount > 1 ? `all ${allCount}` : 'one')} {noun}
          </span>
        </SelectableButton>

        <SelectableButton
          classes={filterClasses}
          selected={filterWithRecommendationsOnly}
          disabled={noRecommendations}
          onClick={handleSelectWithRecommendationsOnly}
        >
          <img
            className={clsx(classes.thumbnail, classes.withFilter)}
            src={
              noRecommendations
                ? filteredThumbnailDisabled
                : filterWithRecommendationsOnly
                  ? filteredThumbnailSelected
                  : filteredThumbnail
            }
            alt="Review recommendations"
          />

          <span className={classes.label}>Review recommendations</span>

          {!noRecommendations && (
            <span className={classes.sublabel}>
              {filteredCount} of {allCount} {noun}
            </span>
          )}
        </SelectableButton>
      </section>

      {allRecommendations && (
        <section className={classes.annotationContainer}>
          <span className={classes.annotationTitle}>Andy found ways to strengthen every {nounSingular}</span>

          <span className={classes.annotation}>
            That’s very common — many {planLabel} {nounPlural} benefit from small refinements to make expectations
            clearer and progress easier to track.
          </span>
        </section>
      )}

      {noRecommendations && (
        <section className={classes.annotationContainer}>
          <span className={classes.annotationTitle}>Nice work — your {nounPlural} look solid!</span>

          <span className={classes.annotation}>
            Andy reviewed {childName}’s {nounPlural} and didn’t find any areas that need clarification or improvement
            based on best practices for well-written {planLabel} {nounPlural}. That doesn’t mean the {nounPlural} can’t
            be discussed — just that they’re clear, measurable, and aligned as written.
          </span>
        </section>
      )}
    </section>
  )
}

FilterSelector.propTypes = {
  className: PropTypes.string,
  planType: PropTypes.oneOf(PLAN_TYPES), // The type of plan for which we’re picking the filter.
  all: PropTypes.array, // The full list of available items.
  filtered: PropTypes.array, // The filtered list of items.
  childName: PropTypes.string, // The child’s name.
  nounSingular: PropTypes.string, // The user-readable string for the item list being filtered, singular.
  nounPlural: PropTypes.string, // The user-readable string for the item list being filtered, plural.
  onChange: PropTypes.func // Function to call when the filter changes.
}

export default FilterSelector
